import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBodyScrollLock } from '../utils/useBodyScrollLock';
import '../styles/Navbar.css';

const Navbar = ({ apiBaseUrl }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  useBodyScrollLock(isNavOpen);
  const { isAuthenticated, userRoles, refreshAuth } = useAuth();
  const [visibilityRole, setVisibilityRole] = useState('Default');
  const [hasExplicitRole, setHasExplicitRole] = useState(false);
  const [hasExclusiveRole, setHasExclusiveRole] = useState(false);
  const [explicitHoldState, setExplicitHoldState] = useState('idle'); // 'idle', 'shaking', 'ready'
  const explicitHoldTimer = useRef(null);
  const explicitHoldStart = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const updateVisibilityIcon = (roles) => {
      if (roles.includes('view-logged-out')) setVisibilityRole('Logged Out');
      else if (roles.includes('view-allowed')) setVisibilityRole('Allowed');
      else if (roles.includes('view-admin')) setVisibilityRole('Admin');
      else if (roles.includes('view-hidden')) setVisibilityRole('Hidden');
      else setVisibilityRole('Default');
    };
    updateVisibilityIcon(userRoles);

    setHasExplicitRole(userRoles.includes('view-explicit'));
    setHasExclusiveRole(userRoles.includes('view-explicit-exclusive'));

    const scrollBtn = document.getElementById('scrollToTopBtn');
    const handleScroll = () => {
      if (window.scrollY > 300) scrollBtn.classList.add('visible');
      else scrollBtn.classList.remove('visible');
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [userRoles]);

  const toggleNavbar = () => setIsNavOpen(prev => !prev);

  const toggleVisibilityRole = () => {
    fetch(`${apiBaseUrl}/users/toggle_visibility_role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        refreshAuth();
      })
      .catch(err => console.error('Error toggling visibility role:', err));
  };

  const toggleExplicitRole = () => {
    fetch(`${apiBaseUrl}/users/toggle_explicit_role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        refreshAuth();
      })
      .catch(err => console.error('Error toggling explicit role:', err));
  };

  const toggleExclusiveRole = () => {
    fetch(`${apiBaseUrl}/users/toggle_explicit_exclusive_role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        refreshAuth();
        setExplicitHoldState('idle');
      })
      .catch(err => console.error('Error toggling exclusive role:', err));
  };

  const handleExplicitMouseDown = (e) => {
    e.preventDefault();
    
    // If already in exclusive mode, just toggle it off with a click
    if (hasExclusiveRole) {
      return;
    }
    
    explicitHoldStart.current = Date.now();
    
    // Start shaking after 1 second
    explicitHoldTimer.current = setTimeout(() => {
      setExplicitHoldState('shaking');
      
      // After 4 more seconds (5 total), trigger ready state
      explicitHoldTimer.current = setTimeout(() => {
        setExplicitHoldState('ready');
      }, 4000);
    }, 1000);
  };

  const handleExplicitMouseUp = (e) => {
    e.preventDefault();
    
    if (explicitHoldTimer.current) {
      clearTimeout(explicitHoldTimer.current);
    }
    
    const holdDuration = Date.now() - (explicitHoldStart.current || Date.now());
    
    // If in exclusive mode, any click turns it off
    if (hasExclusiveRole) {
      toggleExclusiveRole();
      return;
    }
    
    // If held for exactly 5 seconds (ready state), enable exclusive mode
    if (explicitHoldState === 'ready') {
      toggleExclusiveRole();
      return;
    }
    
    // If it was a short click (less than 1 second), toggle regular explicit
    if (holdDuration < 1000) {
      toggleExplicitRole();
    }
    
    setExplicitHoldState('idle');
  };

  const handleExplicitMouseLeave = () => {
    if (explicitHoldTimer.current) {
      clearTimeout(explicitHoldTimer.current);
    }
    if (!hasExclusiveRole) {
      setExplicitHoldState('idle');
    }
  };

  const getExplicitIconClass = () => {
    if (hasExclusiveRole) return 'explicit-exclusive';
    if (explicitHoldState === 'ready') return 'explicit-ready';
    if (explicitHoldState === 'shaking') return 'explicit-shaking';
    return '';
  };

  const getExplicitIcon = () => {
    if (hasExclusiveRole) return 'lock-open-solid.svg';
    if (hasExplicitRole) return 'lock-open-solid.svg';
    return 'lock-solid.svg';
  };

  const handleLogout = () => {
    fetch(`${apiBaseUrl}/logout`, { credentials: 'include' })
      .then(() => {
        refreshAuth();
        navigate('/login');
      })
      .catch(err => console.error('Error logging out:', err));
  };

  const closeMenuOnTouch = (e) => {
    e.stopPropagation();
    setIsNavOpen(false);
  };

  const navCategories = [
    {
      label: 'Steam Games',
      icon: 'steam.svg',
      path: '/media-list/SteamGame',
      subItems: null,
    },
    {
      label: 'Other Games',
      icon: 'gamepad-solid.svg',
      path: '/media-list/PhysicalGame',
      subItems: [
        { icon: 'gameboy.svg', label: 'Game Boy', path: '/media-list/GameboyGame' },
        { icon: 'gamepad-solid.svg', label: 'PSP Games', path: '/media-list/PSPGame' },
        { icon: 'nintendo-ds.svg', label: 'Nintendo DS', path: '/media-list/NDSGame' },
        { icon: 'nintendoswitch.svg', label: 'Nintendo Switch', path: '/media-list/SwitchGame' },
        { icon: 'playstation.svg', label: 'PSVita Games', path: '/media-list/VitaGame' },
      ],
    },
    {
      label: 'Shows & Movies',
      icon: 'clapperboard-solid.svg',
      path: '/media-list/ShowsAndMovies',
      subItems: [
        { icon: 'tv-solid.svg', label: 'TV Shows', path: '/media-list/TV' },
        { icon: 'film-solid.svg', label: 'Movies', path: '/media-list/Movie' },
        { icon: 'clapperboard-solid.svg', label: 'Anime', path: '/media-list/Anime' },
      ],
    },
    {
      label: 'Literature',
      icon: 'book-solid.svg',
      path: '/media-list/Literature',
      subItems: [
        { icon: 'book-open-solid-full.svg', label: 'Manga', path: '/media-list/Manga' },
        { icon: 'book-skull-solid-full.svg', label: 'Comics', path: '/media-list/Comics' },
        { icon: 'book-solid.svg', label: 'Books', path: '/media-list/Books' },
      ],
    },
    {
      label: 'Music',
      icon: 'compact-disc-solid.svg',
      path: '/media-list/Music',
      subItems: [
        { icon: 'record-vinyl-solid.svg', label: 'Vinyl', path: '/media-list/Vinyl' },
        { icon: 'compact-disc-solid.svg', label: 'CD', path: '/media-list/CD' },
      ],
    },
    {
      label: 'More',
      icon: 'list-solid.svg',
      path: '/overview',
      subItems: null,
    },
  ];

  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [hoverDropdown, setHoverDropdown] = useState(null);
  const location = useLocation();

  // Close mobile dropdown when route changes
  useEffect(() => {
    setOpenMobileDropdown(null);
    setIsNavOpen(false);
  }, [location.pathname]);

  const toggleMobileDropdown = (label, e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMobileDropdown(prev => prev === label ? null : label);
  };

  return (
    <>
      <nav className={`navbar ${isNavOpen ? 'nav-open' : ''}`}>
        <div className="hamburger-toggle only-mobile" onClick={toggleNavbar}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div className="nav-left only-desktop">
          <Link to="/" className="nav-item">
            <img src="/static/icons/house-solid.svg" className="icon" alt="Home" /> Home
          </Link>
          <Link to="/add-media" className="nav-item">
            <img src="/static/icons/plus-solid.svg" className="icon" alt="Add media" /> Add media
          </Link>
          <Link to="/activityfeed" className="nav-item" onClick={() => setIsNavOpen(false)}>
            <img src="/static/icons/newspaper-solid.svg" className="icon" alt="Activity Feed" /> Feed
          </Link>
        </div>

        <div className={`nav-center ${isNavOpen ? 'nav-open' : ''}`} id="nav-center">
          <div className="nav-buttons">
            <div className="only-mobile nav-buttons-extra">
              <Link to="/" className="nav-item" onClick={() => setIsNavOpen(false)}>
                <img src="/static/icons/house-solid.svg" className="icon" alt="Home" /> Home
              </Link>
              <Link to="/add-media" className="nav-item" onClick={() => setIsNavOpen(false)}>
                <img src="/static/icons/plus-solid.svg" className="icon" alt="Add media" /> Add media
              </Link>
              <Link to="/activityfeed" className="nav-item" onClick={() => setIsNavOpen(false)}>
                <img src="/static/icons/newspaper-solid.svg" className="icon" alt="Activity Feed" /> Feed
              </Link>
              <Link to="/search" className="nav-item" onClick={() => setIsNavOpen(false)}>
                <img src="/static/icons/magnifying-glass-solid.svg" className="icon" alt="Search" /> Search
              </Link>
              <hr style={{ width: '100%' }} />
            </div>

            {navCategories.map((cat) => (
              <div 
                key={cat.label} 
                className={`nav-category${cat.subItems ? ' has-dropdown' : ''}`}
                onMouseEnter={() => cat.subItems && setHoverDropdown(cat.label)}
                onMouseLeave={() => setHoverDropdown(null)}
              >
                <div className="nav-category-row">
                  {/* Desktop: top-level label is a link (or non-link if no path) */}
                  {cat.path ? (
                    <Link
                      to={cat.path}
                      className="nav-item nav-category-label"
                      onClick={() => setIsNavOpen(false)}
                    >
                      <img src={`/static/icons/${cat.icon}`} className="icon" alt={cat.label} />
                      <span className="nav-label-text">{cat.label}</span>
                      {cat.subItems && (
                        <img src="/static/icons/caret-down-solid.svg" className="icon nav-caret only-desktop" alt="expand" />
                      )}
                    </Link>
                  ) : (
                    <div className="nav-item nav-category-label">
                      <img src={`/static/icons/${cat.icon}`} className="icon" alt={cat.label} />
                      <span className="nav-label-text">{cat.label}</span>
                      {cat.subItems && (
                        <img src="/static/icons/caret-down-solid.svg" className="icon nav-caret only-desktop" alt="expand" />
                      )}
                    </div>
                  )}

                  {/* Mobile: make the label clickable to expand */}
                  {cat.subItems && (
                    <button
                      className="nav-mobile-toggle only-mobile"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenMobileDropdown(prev => prev === cat.label ? null : cat.label);
                      }}
                      aria-label={`Expand ${cat.label}`}
                    >
                      <img
                        src="/static/icons/caret-down-solid.svg"
                        className={`icon nav-caret-mobile ${openMobileDropdown === cat.label ? 'rotated' : ''}`}
                        alt="expand"
                      />
                    </button>
                  )}
                </div>

                {/* Desktop dropdown (shown on hover via state or CSS) */}
                {cat.subItems && (
                  <div className={`nav-dropdown-menu only-desktop ${hoverDropdown === cat.label ? 'visible' : ''}`}>
                    {cat.subItems.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className="nav-item nav-dropdown-item"
                        onClick={() => setIsNavOpen(false)}
                      >
                        <img src={`/static/icons/${sub.icon}`} className="icon" alt={sub.label} />
                        <span>{sub.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Mobile accordion with "All" option */}
                {cat.subItems && (
                  <div className={`nav-mobile-subitems only-mobile ${openMobileDropdown === cat.label ? 'open' : ''}`}>
                    {cat.path && (
                      <Link
                        to={cat.path}
                        className="nav-item nav-subitem nav-all"
                        onClick={() => setIsNavOpen(false)}
                      >
                        <img src={`/static/icons/${cat.icon}`} className="icon" alt="All" />
                        <span>All {cat.label}</span>
                      </Link>
                    )}
                    {cat.subItems.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className="nav-item nav-subitem"
                        onClick={() => setIsNavOpen(false)}
                      >
                        <img src={`/static/icons/${sub.icon}`} className="icon" alt={sub.label} />
                        <span>{sub.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {isAuthenticated && userRoles.includes('admin') && (
              <div className="only-mobile nav-buttons-extra">
                <hr style={{ width: '100%' }} />
                <Link to="/admin" className="nav-item" onClick={() => setIsNavOpen(false)}>
                  <img src="/static/icons/screwdriver-wrench-solid.svg" className="icon" alt="Manage Users" /> Admin
                </Link>
                <div className="nav-item" onClick={toggleVisibilityRole}>
                  <img
                    src={`/static/icons/${
                      visibilityRole === 'Logged Out' ? 'user-slash-solid.svg' :
                      visibilityRole === 'Allowed' ? 'eye-low-vision-solid.svg' :
                      visibilityRole === 'Admin' ? 'eye-solid.svg' :
                      visibilityRole === 'Hidden' ? 'ghost-solid.svg' :
                      'eye-slash-solid.svg'
                    }`}
                    className="icon"
                    alt="Visibility"
                  />
                  <span>Visibility: {visibilityRole}</span>
                </div>
                {(userRoles.includes('admin') || userRoles.includes('view-allowed')) && (
                  <div 
                    className={`nav-item explicit-toggle ${getExplicitIconClass()}`}
                    onMouseDown={handleExplicitMouseDown}
                    onMouseUp={handleExplicitMouseUp}
                    onMouseLeave={handleExplicitMouseLeave}
                    onTouchStart={handleExplicitMouseDown}
                    onTouchEnd={handleExplicitMouseUp}
                  >
                    <img
                      src={`/static/icons/${getExplicitIcon()}`}
                      className={`icon ${getExplicitIconClass()}`}
                      alt="Explicit Content"
                    />
                    <span>Explicit: {hasExclusiveRole ? 'Exclusive' : hasExplicitRole ? 'Enabled' : 'Disabled'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="nav-right nav-buttons admin-action">
          {isAuthenticated && userRoles.includes('admin') && (
            <>
              <Link to="/admin" className="nav-item desktop-only">
                <img src="/static/icons/screwdriver-wrench-solid.svg" className="icon" alt="Manage Users" />
              </Link>
              <div className="nav-item desktop-only" onClick={toggleVisibilityRole}>
                <img
                  src={`/static/icons/${
                    visibilityRole === 'Logged Out' ? 'user-slash-solid.svg' :
                    visibilityRole === 'Allowed' ? 'eye-low-vision-solid.svg' :
                    visibilityRole === 'Admin' ? 'eye-solid.svg' :
                    visibilityRole === 'Hidden' ? 'ghost-solid.svg' :
                    'eye-slash-solid.svg'
                  }`}
                  className="icon"
                  alt="Visibility"
                />
              </div>
            </>
          )}
          {isAuthenticated && (userRoles.includes('admin') || userRoles.includes('view-allowed')) && (
            <div 
              className={`nav-item desktop-only explicit-toggle ${getExplicitIconClass()}`}
              onMouseDown={handleExplicitMouseDown}
              onMouseUp={handleExplicitMouseUp}
              onMouseLeave={handleExplicitMouseLeave}
              onTouchStart={handleExplicitMouseDown}
              onTouchEnd={handleExplicitMouseUp}
            >
              <img
                src={`/static/icons/${getExplicitIcon()}`}
                className={`icon ${getExplicitIconClass()}`}
                alt="Explicit Content"
              />
            </div>
          )}
          <Link to="/search" className="nav-item nav-search-item desktop-only" title="Search" aria-label="Search">
            <img src="/static/icons/magnifying-glass-solid.svg" className="icon" alt="Search" />
          </Link>
          {isAuthenticated ? (
            <div className="nav-item" onClick={handleLogout}>
              <img src="/static/icons/right-from-bracket-solid.svg" className="icon" alt="Logout" /> Logout
            </div>
          ) : (
            <Link to="/login" className="nav-item">
              <img src="/static/icons/right-to-bracket-solid.svg" className="icon" alt="Login" /> Login
            </Link>
          )}
        </div>
      </nav>

      {isNavOpen && (
        <div
          className="nav-overlay"
          onClick={closeMenuOnTouch}
          onTouchStart={closeMenuOnTouch}
        ></div>
      )}

      <button id="scrollToTopBtn" className="scroll-to-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <img src="/static/icons/arrow-up-solid.svg" className="icon" alt="Scroll to top" />
      </button>
    </>
  );
};

export default Navbar;